/**
 * „Zahlungsplan anpassen" — EIN Fenster für zwei Orte (Punkt 29 P2, Entscheidung 2): die
 * Abrechnungsseite (Knopf am Deckungshinweis, Z2.3) und die Vorgangsseite (Hinweis nach einer
 * geänderten Annahme). Bis P2 lebte der Inhalt direkt in `pages/abrechnung.tsx`; hierher verschoben,
 * damit beide Orte wörtlich dieselbe Vorschau und denselben Aufruf haben — keine zweite Rechnung.
 *
 * Die Vorschau rechnet nicht selbst (`pruefeAnpassung` → `verteileNeu`), der Aufruf schickt keinen
 * Betrag (die Route rechnet), und nach dem Anpassen wird NICHTS versendet: ein entwerteter
 * Zahlungslink wird erst durch einen weiteren Klick des Betreibers neu erzeugt und verschickt.
 */
import { useState } from "react";
import { apiMutateMitZusatz } from "../../hooks/useApi";
import { pruefeAnpassung, rechnungskorrekturText, type AnpassungsZeile, type Rechnungskorrektur } from "../../lib/zahlungsanpassung-anzeige";
import { formatDate, money } from "../format";
import { Button, Checkbox, Drawer, ErrorNote, Icon } from "../components";

/** Punkt 29 P3: eine Anzahlungsrechnung, die erst nach Bestaetigung ersetzt wird (409 der Route). */
type ZuErsetzen = { paymentId: string; rechnungsnummer: string | null; altCents: number; neuCents: number };

export function ZahlungsplanAnpassenDrawer({
  open,
  onClose,
  eventId,
  amountTotalCents,
  payments,
  onAngepasst,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string;
  amountTotalCents: number | null | undefined;
  payments: readonly AnpassungsZeile[];
  onAngepasst: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // M-3: hat die Anpassung eine Anzahlungsrechnung ersetzt (oder es versucht), bleibt das Fenster
  // offen und sagt, was mit den Belegen geschah — sonst erfaehrt der Betreiber es nie.
  const [korrektur, setKorrektur] = useState<Rechnungskorrektur[] | null>(null);
  // Punkt 29 P3 (05.10.2026): Storno + neue Rechnung erst nach ausdruecklicher Bestaetigung. Die
  // Route nennt die betroffenen Rechnungen (409), hier wird nachgefragt; erst dann geht das Flag mit.
  const [rueckfrage, setRueckfrage] = useState<{ rechnungen: ZuErsetzen[]; bestaetigung: string } | null>(null);
  const [bestaetigt, setBestaetigt] = useState(false);

  async function ausfuehren() {
    setBusy(true);
    setFehler(null);
    try {
      const res = await apiMutateMitZusatz<unknown, { rechnungskorrektur?: Rechnungskorrektur[] }>(
        `/api/events/${eventId}/payment-plan/adjust`,
        "POST",
        rueckfrage && bestaetigt ? { anzahlungsrechnungErsetzen: rueckfrage.bestaetigung } : {},
      );
      const k = res?.rechnungskorrektur ?? [];
      onAngepasst();
      if (k.length > 0) setKorrektur(k);
      else onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const e = err as { code?: string; body?: { rechnungen?: ZuErsetzen[]; bestaetigung?: string } };
      if (e.code === "anzahlungsrechnung_bestaetigen") {
        // Stand geaendert? Die Route schickt dann eine neue Kennung — der Betreiber bestaetigt neu.
        setRueckfrage({ rechnungen: e.body?.rechnungen ?? [], bestaetigung: e.body?.bestaetigung ?? "" });
        setBestaetigt(false);
        return;
      }
      setFehler(
        e.code === "conflict"
          ? "Der Zahlungsplan hat sich zwischenzeitlich geändert – bitte die Seite neu laden und erneut anpassen."
          : /link_deactivation_failed|502/i.test(msg)
            ? "Die aktiven Zahlungslinks konnten nicht deaktiviert werden – es wurde nichts geändert. Bitte später erneut versuchen."
            : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  const schliessen = () => {
    setFehler(null);
    setKorrektur(null);
    setRueckfrage(null);
    setBestaetigt(false);
    onClose();
  };
  const vorschau = pruefeAnpassung(amountTotalCents, payments);

  return (
    <Drawer
      open={open}
      onClose={schliessen}
      title="Zahlungsplan anpassen"
      subtitle={amountTotalCents ? `Angebotssumme ${money(amountTotalCents)}` : "Noch keine Angebotssumme – zuerst eine Variante wählen."}
    >
      <div className="space-y-4">
        <p role="status" className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          {vorschau.meldung}
        </p>

        {vorschau.stufen.length > 0 && (
          <ul className="space-y-2" aria-label="Stufen, die sich ändern">
            {vorschau.stufen.map((s) => (
              <li key={s.id} className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <div className="min-w-0">
                    <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{s.bezeichnung}</p>
                    <p className="mt-0.5 font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">
                      {s.faellig ? `Fällig ${formatDate(s.faellig)}` : "Ohne Fälligkeit"}
                      {s.linkStirbt ? " · Zahlungslink wird ungültig" : ""}
                    </p>
                  </div>
                  <p className="shrink-0 font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    {money(s.altCents)} → {money(s.neuCents)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {vorschau.linkwarnung && (
          <p className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-2.5 py-2 font-body-sm text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]">
            <Icon name="warning" className="mt-px shrink-0 text-[14px]" />
            <span className="min-w-0 break-words">{vorschau.linkwarnung}</span>
          </p>
        )}

        {vorschau.ausfuehrbar && (
          <p className="flex items-start gap-1.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
            <Icon name="info" className="mt-px shrink-0 text-[14px]" />
            <span className="min-w-0 break-words">
              Bezahlte Anzahlungen und ihre Rechnungen bleiben unverändert. Eine noch unbezahlte Anzahlungsrechnung wird nach Rückfrage per Gutschrift
              storniert und über den neuen Betrag neu erstellt (Versand erst nach Freigabe). Ein neuer Zahlungslink wird nicht automatisch
              verschickt.
            </span>
          </p>
        )}

        {rueckfrage && !korrektur && (
          <div role="alert" data-testid="anzahlungsrechnung-rueckfrage" className="rounded-lg bg-[#F0EAE0] px-3 py-2.5 font-body-sm text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]">
            <p className="font-semibold">Anzahlungsrechnung ersetzen?</p>
            <ul className="mt-1 space-y-0.5">
              {rueckfrage.rechnungen.map((r) => (
                <li key={r.paymentId} className="break-words">
                  Rechnung {r.rechnungsnummer ?? "(ohne Nummer)"} über {money(r.altCents)} wird per Gutschrift storniert, eine neue über{" "}
                  {money(r.neuCents)} erstellt.
                </li>
              ))}
            </ul>
            <p className="mt-1">Beide Belege gehen nicht automatisch an den Gast – die Mail liegt danach zur Freigabe bereit.</p>
            <label className="mt-1.5 flex items-center gap-2 font-semibold">
              <Checkbox checked={bestaetigt} onChange={(ev) => setBestaetigt(ev.target.checked)} />
              Ja, stornieren und neu erstellen
            </label>
          </div>
        )}

        {fehler && <ErrorNote message={fehler} />}

        {korrektur && (
          <ul className="space-y-2" aria-label="Anzahlungsrechnungen">
            {korrektur.map((k) => {
              const t = rechnungskorrekturText(k);
              return (
                <li key={k.neuPaymentId} role="status" className="flex items-start gap-1.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  <Icon name={t.ok ? "check_circle" : "warning"} className="mt-px shrink-0 text-[16px]" />
                  <span className="min-w-0 break-words">{t.text}</span>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={schliessen}>
            {korrektur ? "Schließen" : "Abbrechen"}
          </Button>
          {!korrektur && (
            <Button type="button" variant="primary" disabled={busy || !vorschau.ausfuehrbar || (rueckfrage !== null && !bestaetigt)} onClick={() => void ausfuehren()}>
              {busy ? "Passt an …" : "Plan jetzt anpassen"}
            </Button>
          )}
        </div>
      </div>
    </Drawer>
  );
}
